import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Maximize2, Minimize2, Settings, X, Fullscreen } from "lucide-react";
import Color from "colorjs.io";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { deriveSecondary, GenieChatInputWeb } from "./GenieChatInputWeb";
import { GenieAttachmentCard, GenieAttachmentPending } from "./GenieAttachmentCard";
import { GenieMessageMarkdown } from "./GenieMessageMarkdown";
import { GenieMist } from "./GenieMist";
import { ConversationWelcome } from "./ConversationWelcome";
import { ImageAttachmentPreviewWeb, WebImageAttachment } from "./ImageAttachmentPreviewWeb";

import type { TranscriptMessage } from "./TranscriptPanel";
import type { WidgetChatStyle, WidgetActionButtonStyle, ChatEmptyStateElement } from "@/types/ui/branding";
import { useChatTheme, ensureReadableOnWhite } from "@/hooks/voice/useChatTheme";
import { useStickToBottom } from "@/hooks/ui/useStickToBottom";
import { useMediaLookupPending } from "@/hooks/voice/useMediaLookupPending";
import { ChatThemePicker } from "./ChatThemePicker";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const CHAT_SETTINGS_KEY = "genie_chat_overlay_settings_v1";
const FONT_SIZE_PRESETS = [13, 15, 17, 19, 21];

// HG brand fallback when no tenant brand color is set.
const HG_BRAND_PRIMARY = "#4E9CFF";
const HG_BRAND_SECONDARY = "#FF4DB8";

const getContrastTextColor = (hex: string): string => {
  const color = new Color(hex);
  const hsl = color.to("hsl");
  const lightness = hsl.coords[2];
  return lightness > 70 ? "#1A1A1A" : "#FFFFFF";
};

interface GenieTextChatOverlayWebProps {
  /** Website and preview use the roomy, Genie-branded welcome composition. */
  welcomeLayout?: "page";
  brandColor: string;
  agentName?: string;
  messages: TranscriptMessage[];
  inlineMessage: string;
  onChangeInlineMessage: (value: string) => void;
  onSend: () => void;
  isConnecting: boolean;
  isSpeaking: boolean;
  isTextOnlyMode: boolean;
  isAllAudioMuted: boolean;
  onToggleAllAudioMute: () => void;
  /**
   * True when the conversation was initiated from text input (text-only start).
   * Controls whether header button shows \"End chat\" vs simple close.
   */
  startedFromText: boolean;
  onCloseOverlay: () => void;
  onEndChat: () => void;
  attachments?: WebImageAttachment[];
  onRemoveAttachment?: (id: string) => void;
  onOpenFilePicker?: () => void;
  onUpdateAttachmentAnalysis?: (id: string, text: string) => void;
  imageUploadPendingCount?: number;
  imageUploadFailedCount?: number;
  imageUploadSaving?: boolean;
  onRetryImageUploads?: () => void;
  /** When true, renders without outer border/shadow and stretches to parent (e.g. inside Drawer). */
  inDrawer?: boolean;
  /** Desktop only: whether chat is in "focus" mode (genie shrinks, chat widens). */
  chatExpanded?: boolean;
  /** Desktop only: toggle expand/collapse. When provided, renders the expand button. */
  onToggleExpand?: () => void;
  /** Open the full-page transcript without changing the active session. */
  onOpenFullTextView?: () => void;
  /** Optional agent secondary brand color, used for the input's gradient border. */
  secondaryColor?: string | null;
  /**
   * Optional content rendered in the header's left slot (where the title used
   * to sit). Used by the mobile drawer / embedded widget to surface mic +
   * volume controls in the otherwise-empty header band.
   */
  headerLeftSlot?: React.ReactNode;
  /**
   * True when the voice session is live. When true the header shows an
   * explicit "End" button so the user can quit the conversation from the
   * chat view (they otherwise can't see the avatar's End Conversation CTA).
   */
  voiceConnected?: boolean;
  /** End the active voice conversation. Required if voiceConnected. */
  onEndConversation?: () => void;
  /**
   * True when the parent layout is force-keeping the drawer open (e.g. the
   * post-conversation feedback view). In that case the close X is a trap —
   * clicking it just bounces back — so hide it.
   */
  hideClose?: boolean;
  /**
   * When true, the chat overlay does NOT render its own header band.
   * Used by the embedded widget where the parent shell already provides
   * the chrome (Genie mark, name, voice controls, close). Without this the
   * widget shows two stacked headers.
   */
  hideHeader?: boolean;
  /**
   * Initial chat font size preset index used when the user has no stored
   * preference. Default is 1 (14px). The embedded widget passes 0 (12px)
   * so the popup defaults to a more compact reading size. Local storage
   * still wins if the user has explicitly chosen a size before.
   */
  defaultFontSizeIndex?: number;
  /**
   * Hard override for the chat font size in pixels. Bypasses both the
   * preset lookup and the localStorage preference. Used by the embedded
   * widget to lock in a compact size that's smaller than any preset
   * exposes (11px, vs the minimum preset of 12px).
   */
  fontSizeOverride?: number;
  /**
   * Visual presentation. "bare" (default) is the transparent panel used inside
   * the mobile drawer / desktop two-column layout. "card" wraps the chat in a
   * summoned conversation surface — brand-gradient hairline border, soft shadow,
   * genie-mist backdrop, and an identity header (avatar + name + live status).
   * Used for the desktop full-screen text chat so it reads as a solid, branded
   * panel rather than floating text on an empty page.
   */
  surface?: "bare" | "card";
  /** Genie logo/mark shown in the card-mode identity header. */
  logoUrl?: string | null;
  /** Popup background to inherit for the `inDrawer` wrapper — same resolved
   *  style (solid/gradient/pattern) as the avatar screen. Embed widget only. */
  backgroundStyle?: React.CSSProperties;
  /** Welcome text color for the page-layout empty state (ConversationWelcome). */
  welcomeTextColor?: string | null;
  /** The chat sits on a dark background (bare/drawer surfaces only — the card is always white). */
  darkSurface?: boolean;
  /** Input pill background color override. Falls back to white. */
  inputBackgroundColor?: string | null;
  /** Empty-state (icon/title/subtitle) visibility, order, alignment, and copy. */
  chatStyle?: WidgetChatStyle | null;
  /** Action-button color overrides — reused for the send button when
   *  chatStyle.sendButtonUseActionStyle is set. */
  actionButtonStyle?: WidgetActionButtonStyle | null;
  /**
   * Rendered as the last item in the message stream — the in-conversation
   * capture moment (see InlineCaptureMoment / useInConversationCapture). A
   * slot rather than a prop bag so this surface stays agnostic of the
   * capture pipeline; the caller owns visibility and state.
   */
  inlineCaptureSlot?: React.ReactNode;
}

export const GenieTextChatOverlayWeb = ({
  welcomeLayout,
  brandColor,
  agentName,
  messages,
  inlineMessage,
  onChangeInlineMessage,
  onSend,
  isConnecting,
  isSpeaking,
  isTextOnlyMode,
  isAllAudioMuted,
  onToggleAllAudioMute,
  startedFromText,
  onCloseOverlay,
  onEndChat,
  attachments = [],
  onRemoveAttachment,
  onOpenFilePicker,
  onUpdateAttachmentAnalysis,
  imageUploadPendingCount = 0,
  imageUploadFailedCount = 0,
  imageUploadSaving = false,
  onRetryImageUploads,
  inDrawer = false,
  chatExpanded = false,
  onToggleExpand,
  onOpenFullTextView,
  secondaryColor,
  headerLeftSlot,
  voiceConnected = false,
  onEndConversation,
  hideClose = false,
  hideHeader = false,
  defaultFontSizeIndex = 1,
  fontSizeOverride,
  surface = "bare",
  logoUrl,
  backgroundStyle,
  welcomeTextColor,
  darkSurface: darkSurfaceProp = false,
  inputBackgroundColor,
  chatStyle,
  actionButtonStyle,
  inlineCaptureSlot,
}: GenieTextChatOverlayWebProps) => {
  const { scrollRef, contentRef, scrollToBottom } = useStickToBottom();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  // The card is always a white sheet, whatever the page behind it.
  const darkSurface = darkSurfaceProp && surface !== "card";
  const {
    themeId,
    setThemeId,
    fontSizeIndex,
    setFontSizeIndex,
    themeColors,
    fontSize: themeFontSize,
    lineHeight,
    userLabelInk,
    genieLabelInk,
  } = useChatTheme({
    storageKey: CHAT_SETTINGS_KEY,
    brandColor,
    secondaryColor,
    defaultFontSizeIndex,
    fontSizePresets: FONT_SIZE_PRESETS,
    darkSurface,
  });
  const fontSize = fontSizeOverride ?? themeFontSize;

  const lastMessage = messages[messages.length - 1];
  // Sending a message always returns to the latest, even after scrolling up.
  const lastMessageIsUser = lastMessage?.isUser === true;
  useEffect(() => {
    if (lastMessageIsUser) scrollToBottom();
  }, [messages.length, lastMessageIsUser, scrollToBottom]);

  const isWaitingForReply = !!lastMessage && lastMessage.isUser && !isConnecting && !isSpeaking;
  const mediaPending = useMediaLookupPending();

  const DEFAULT_EMPTY_STATE_ORDER: ChatEmptyStateElement[] = ["icon", "title", "subtitle"];
  const emptyStateOrder = chatStyle?.elementOrder ?? DEFAULT_EMPTY_STATE_ORDER;
  const emptyStateHidden = new Set(chatStyle?.hiddenElements ?? []);
  const emptyStateAlign = (el: ChatEmptyStateElement) => chatStyle?.elementAlign?.[el] ?? "center";

  const statusText = useMemo(() => {
    if (isConnecting) return "Connecting…";
    if (isSpeaking) return "Genie is responding…";
    if (isWaitingForReply) return "Genie is thinking…";
    return "";
  }, [isConnecting, isSpeaking, isWaitingForReply]);

  // Card-mode chrome. The genie accent mirrors the input pill's gradient end so
  // the border, mist and send button all sing in the same brand key.
  const genieAccent = useMemo(() => {
    const userBrand = brandColor || HG_BRAND_PRIMARY;
    return (
      secondaryColor ||
      (userBrand && userBrand !== HG_BRAND_PRIMARY ? deriveSecondary(userBrand) : HG_BRAND_SECONDARY)
    );
  }, [brandColor, secondaryColor]);

  const cardBrand = brandColor || HG_BRAND_PRIMARY;
  // Border-free sheet: depth comes from one soft, large shadow — not from a
  // gradient hairline boxing the conversation in. The brand lives in the
  // mist, the labels, the markers and the input pill instead.
  const cardShadow = "0 24px 90px -32px rgba(15,23,42,0.28), 0 2px 10px -6px rgba(15,23,42,0.10)";

  // Identity-header status: a calm "Online" at rest, the live working state
  // (connecting / responding / thinking) while the genie is busy.
  const headerActive = statusText.length > 0;
  const headerStatusLabel = statusText || "Online";
  const statusInk = headerActive ? ensureReadableOnWhite(cardBrand) : "#64748B";

  // Shared control cluster (settings, expand, end/close) — rendered on the right
  // of either the bare header band or the card identity header.
  const controlButtons = (
    <div className="flex items-center gap-1 self-center">
      <button
        type="button"
        onClick={() => setIsSettingsOpen((prev) => !prev)}
        className={cn(
          "flex h-8 w-8 items-center justify-center rounded-full transition-colors",
          isSettingsOpen ? "" : "text-[#9CA3AF] hover:text-[#222B45] hover:bg-black/[0.04]",
        )}
        style={isSettingsOpen ? { backgroundColor: brandColor, color: getContrastTextColor(brandColor) } : undefined}
        title="Chat settings"
        aria-label="Chat settings"
      >
        <Settings className="w-4 h-4" />
      </button>
      {onToggleExpand && (
        <button
          type="button"
          onClick={onToggleExpand}
          className="flex h-8 w-8 items-center justify-center rounded-full text-[#9CA3AF] hover:text-[#222B45] hover:bg-black/[0.04] transition-colors"
          title={chatExpanded ? "Restore balanced view" : "Focus chat"}
          aria-label={chatExpanded ? "Restore balanced view" : "Focus chat"}
        >
          {chatExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </button>
      )}
      {onOpenFullTextView && (
        <Button
          type="button"
          variant="ghost"
          onClick={onOpenFullTextView}
          className="min-h-[44px] rounded-full text-gray-600"
        >
          <Fullscreen className="mr-2 h-4 w-4" aria-hidden="true" />
          Full text view
        </Button>
      )}
      {voiceConnected && onEndConversation ? (
        /* Voice session live → explicit End button. Otherwise the user
           has no way to quit the call from the chat view (the avatar's
           End Conversation CTA is hidden behind the chat). */
        <button
          type="button"
          onClick={onEndConversation}
          className="rounded-full bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100 ml-1"
          title="End conversation"
        >
          End
        </button>
      ) : startedFromText ? (
        <button
          type="button"
          onClick={onEndChat}
          className="rounded-full bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100 ml-1"
        >
          End chat
        </button>
      ) : hideClose ? null : (
        <button
          type="button"
          onClick={onCloseOverlay}
          className="flex h-8 w-8 items-center justify-center rounded-full text-[#9CA3AF] hover:text-[#222B45] hover:bg-black/[0.04] transition-colors"
          title="Close chat"
          aria-label="Close chat"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );

  // Card-mode identity header — anchors the conversation with the genie's mark,
  // name and a live status line, so the surface never feels anonymous.
  const identityHeader = (
    <div className="flex items-center gap-3 border-b border-[#F1F4F9] px-5 py-4 sm:px-7">
      <span
        className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-2xl"
        style={{ background: `linear-gradient(135deg, ${cardBrand}1a, ${genieAccent}1a)` }}
      >
        {logoUrl ? (
          <img src={logoUrl} alt="" className="h-8 w-8 rounded-lg object-contain" />
        ) : (
          <GenieIcon
            name="genie-lamp"
            size={20}
            color={cardBrand}
            secondaryColor={genieAccent}
            gradientEnabled
          />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[15px] font-semibold leading-tight text-[#222B45]">{agentName || "Genie"}</p>
        <span className="mt-0.5 flex items-center gap-1.5 text-[12px] leading-tight" style={{ color: statusInk }}>
          <span className="relative flex h-1.5 w-1.5">
            {headerActive && (
              <span
                className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-70"
                style={{ backgroundColor: cardBrand }}
              />
            )}
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full" style={{ backgroundColor: cardBrand }} />
          </span>
          {headerStatusLabel}
        </span>
      </div>
      {controlButtons}
    </div>
  );

  const content = (
    <>
      {/* Header — card mode shows the genie identity band; bare mode keeps the
          minimal control strip (the agent name is already the per-message role
          label, so no title there). When hideHeader is true (embed widget), the
          parent shell owns the chrome and we skip this whole band. */}
      {surface === "card"
        ? identityHeader
        : !hideHeader && (
            <div
              className={cn(
                "flex items-center px-3 py-2",
                headerLeftSlot ? "justify-between gap-2" : "justify-end",
              )}
            >
              {headerLeftSlot ? <div className="flex items-end min-w-0 flex-1">{headerLeftSlot}</div> : null}
              {controlButtons}
            </div>
          )}

      {/* Chat settings panel */}
      <AnimatePresence initial={false}>
        {isSettingsOpen && (
          <motion.div
            key="chat-settings"
            initial={{ opacity: 0, height: 0, y: -4 }}
            animate={{ opacity: 1, height: "auto", y: 0 }}
            exit={{ opacity: 0, height: 0, y: -4 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="overflow-hidden border-y border-slate-100/80 bg-white/95 px-4 py-3 space-y-3"
          >
            <div>
              <p className="text-xs font-medium text-slate-600">Chat text size</p>
              <div className="mt-2 flex items-center justify-between gap-2">
                {FONT_SIZE_PRESETS.map((size, index) => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => setFontSizeIndex(index)}
                    className={cn(
                      "flex-1 rounded-full px-2 py-2.5 text-center text-[11px] font-medium transition-colors",
                      index === fontSizeIndex
                        ? "bg-[#4E9CFF14] text-[#1A1A1A] border border-[#4E9CFF]"
                        : "bg-slate-50 text-slate-600 border border-transparent hover:bg-slate-100",
                    )}
                  >
                    <span style={{ fontSize: size - 1 }}>Aa</span>
                  </button>
                ))}
              </div>
            </div>

            <ChatThemePicker themeId={themeId} onChange={setThemeId} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Messages */}
      <div
        className={cn(
          "relative flex h-0 flex-1 min-h-0 overflow-hidden pt-3 pb-1",
          surface === "card" ? "px-5 sm:px-7" : "px-3",
        )}
      >
        {messages.length === 0 && welcomeLayout === "page" ? (
          <div className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto before:flex-1 after:flex-1">
            <ConversationWelcome brandColor={cardBrand} secondaryColor={genieAccent}
              agentName={agentName} logoUrl={logoUrl} chatStyle={chatStyle} textColor={welcomeTextColor} darkSurface={darkSurface} />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex h-full w-full flex-col items-center justify-center py-16">
            {emptyStateOrder
              .filter((el) => !emptyStateHidden.has(el))
              .map((el) => {
                const align = emptyStateAlign(el);
                const alignClass =
                  align === "left" ? "items-start text-left" : align === "right" ? "items-end text-right" : "items-center text-center";

                if (el === "icon") {
                  return (
                    <div key="icon" className={cn("flex w-full flex-col mb-4", alignClass)}>
                      <span
                        className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-2xl"
                        style={{ background: `linear-gradient(135deg, ${cardBrand}14, ${genieAccent}14)` }}
                      >
                        {logoUrl ? (
                          <img src={logoUrl} alt="" className="h-8 w-8 object-contain" />
                        ) : (
                          <GenieIcon
                            name="genie-lamp"
                            size={24}
                            color={cardBrand}
                            secondaryColor={genieAccent}
                            gradientEnabled
                          />
                        )}
                      </span>
                    </div>
                  );
                }

                if (el === "title") {
                  return (
                    <div key="title" className={cn("flex w-full flex-col mb-1.5", alignClass)}>
                      <p className={cn("text-sm font-semibold", darkSurface ? "text-[#F1F5F9]" : "text-[#222B45]")}>
                        {chatStyle?.titleText || `Ask ${agentName || "your genie"} anything`}
                      </p>
                    </div>
                  );
                }

                return (
                  <div key="subtitle" className={cn("flex w-full flex-col", alignClass)}>
                    <p
                      className={cn(
                        "max-w-[280px] text-xs leading-relaxed",
                        darkSurface ? "text-[#94A3B8]" : "text-[#64748B]",
                      )}
                    >
                      {chatStyle?.subtitleText ||
                        "Type a question, or just speak — the conversation flows here either way."}
                    </p>
                  </div>
                );
              })}
          </div>
        ) : (
          <div
            ref={scrollRef}
            className="brand-scroll h-full w-full min-h-0 overflow-y-auto overscroll-contain pr-1"
          >
            <div ref={contentRef} className={surface === "card" ? "space-y-5 pb-4" : "space-y-3.5"}>
            {messages.map((msg, idx) =>
              (() => {
                if (Array.isArray(msg.imageAttachments) && msg.imageAttachments.length > 0) {
                  const imageAttachments: WebImageAttachment[] = msg.imageAttachments.map(
                    (img, index: number) => ({
                      id: img.id || `${idx}-img-${index}`,
                      previewUrl: img.previewUrl,
                      status: "ready",
                      analysisText: img.analysisText,
                    }),
                  );

                  return (
                    <div key={`${msg.timestamp.getTime()}-${idx}`} className="flex justify-start">
                      <ImageAttachmentPreviewWeb
                        attachments={imageAttachments}
                        onRemove={() => undefined}
                        isPreviewOnly={true}
                      />
                    </div>
                  );
                }

                if (msg.attachment) {
                  return (
                    <div key={`${msg.timestamp.getTime()}-${idx}`} className="flex justify-start px-1">
                      <GenieAttachmentCard attachment={msg.attachment} brandColor={genieLabelInk} />
                    </div>
                  );
                }

                const labelText = msg.isUser ? "You" : agentName || "Genie";
                const labelColor = msg.isUser ? userLabelInk : genieLabelInk;
                const timestamp = msg.timestamp.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                });

                // Shared label row. On the user side we reverse the row so the
                // role label sits flush against the outer (right) edge and the
                // hover-revealed timestamp slides in on the inside.
                const labelRow = (
                  <div
                    className={cn(
                      "mb-1 flex items-baseline gap-1.5",
                      msg.isUser ? "flex-row-reverse" : "flex-row",
                    )}
                  >
                    <span
                      className="text-[11px] font-medium"
                      style={{ color: labelColor }}
                    >
                      {labelText}
                    </span>
                    <span
                      className="text-[11px] font-normal opacity-0 transition-opacity group-hover:opacity-100"
                      style={{ color: themeColors.systemTextColor }}
                    >
                      {timestamp}
                    </span>
                  </div>
                );

                if (themeColors.style === "plain") {
                  // IMPRINT: text sits on the page. No bubble, no border, no shadow.
                  // Role indicated by alignment + a tiny brand-tinted label.
                  return (
                    <div
                      key={`${msg.timestamp.getTime()}-${idx}`}
                      className={cn(
                        "group flex flex-col px-1 py-1.5",
                        msg.isUser ? "items-end text-right" : "items-start text-left",
                      )}
                    >
                      {labelRow}
                      {msg.isUser ? (
                        /* User side gets a soft brand-tinted pill — enough shape
                           to read as "you said this" at a glance without boxing
                           the genie's replies, which stay open on the page. */
                        <div
                          className="max-w-[88%] whitespace-pre-wrap rounded-2xl rounded-tr-md px-4 py-2.5 text-left font-sans"
                          style={{
                            fontSize,
                            lineHeight: `${lineHeight}px`,
                            letterSpacing: "-0.006em",
                            color: themeColors.userTextColor,
                            backgroundColor: `${themeColors.userLabelColor}14`,
                          }}
                        >
                          {msg.message}
                        </div>
                      ) : (
                        <div className="max-w-[88%]">
                          <GenieMessageMarkdown
                            text={msg.message}
                            fontSize={fontSize}
                            lineHeight={lineHeight}
                            color={themeColors.genieTextColor}
                            accentColor={genieLabelInk}
                          />
                        </div>
                      )}
                    </div>
                  );
                }

                if (themeColors.style === "edged") {
                  // EDGE: no bg. A 2px brand-color rule down the gutter side of each message.
                  return (
                    <div
                      key={`${msg.timestamp.getTime()}-${idx}`}
                      className={cn(
                        "group flex",
                        msg.isUser ? "justify-end" : "justify-start",
                      )}
                    >
                      <div
                        className={cn(
                          "max-w-[88%] py-1",
                          msg.isUser
                            ? "border-r-2 pr-3 text-right"
                            : "border-l-2 pl-3 text-left",
                        )}
                        style={{
                          borderColor: msg.isUser
                            ? themeColors.userRuleColor
                            : themeColors.genieRuleColor,
                        }}
                      >
                        {labelRow}
                        {msg.isUser ? (
                          <div
                            className="whitespace-pre-wrap font-sans"
                            style={{
                              fontSize,
                              lineHeight: `${lineHeight}px`,
                              letterSpacing: "-0.006em",
                              color: themeColors.userTextColor,
                            }}
                          >
                            {msg.message}
                          </div>
                        ) : (
                          <GenieMessageMarkdown
                            text={msg.message}
                            fontSize={fontSize}
                            lineHeight={lineHeight}
                            color={themeColors.genieTextColor}
                            accentColor={genieLabelInk}
                          />
                        )}
                      </div>
                    </div>
                  );
                }

                // WHISPER (tinted): barely-there bubble. Brand tint, no border, no shadow.
                return (
                  <div
                    key={`${msg.timestamp.getTime()}-${idx}`}
                    className={cn(
                      "group flex",
                      msg.isUser ? "justify-end" : "justify-start",
                    )}
                  >
                    <div
                      className={cn(
                        "max-w-[88%] rounded-2xl px-4 py-3",
                        msg.isUser ? "text-right" : "text-left",
                      )}
                      style={{
                        backgroundColor: msg.isUser
                          ? themeColors.userBubbleBg
                          : themeColors.genieBubbleBg,
                        color: msg.isUser
                          ? themeColors.userTextColor
                          : themeColors.genieTextColor,
                      }}
                    >
                      {labelRow}
                      {msg.isUser ? (
                        <div
                          className="whitespace-pre-wrap font-sans"
                          style={{
                            fontSize,
                            lineHeight: `${lineHeight}px`,
                            letterSpacing: "-0.006em",
                          }}
                        >
                          {msg.message}
                        </div>
                      ) : (
                        <GenieMessageMarkdown
                          text={msg.message}
                          fontSize={fontSize}
                          lineHeight={lineHeight}
                          color={themeColors.genieTextColor}
                          accentColor={genieLabelInk}
                        />
                      )}
                    </div>
                  </div>
                );
              })(),
            )}
            {mediaPending && (
              <div className="flex justify-start px-1">
                <GenieAttachmentPending brandColor={genieLabelInk} />
              </div>
            )}
            {inlineCaptureSlot}
            </div>
          </div>
        )}
        {/* Card mode: content fades out under the composer instead of clipping mid-line. */}
        {surface === "card" && messages.length > 0 && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white to-transparent" />
        )}
      </div>

      {/* Attachments + input */}
      <div className={cn("space-y-2 pt-2", surface === "card" ? "px-5 pb-5 sm:px-7" : "px-3 pb-3")}>
        {attachments && attachments.length > 0 && onRemoveAttachment && (
          <ImageAttachmentPreviewWeb
            attachments={attachments}
            onRemove={onRemoveAttachment}
            onUpdateAnalysis={onUpdateAttachmentAnalysis}
          />
        )}
        {imageUploadPendingCount > 0 && (
          <div className="flex items-center gap-2 rounded-xl border border-[#E5EAF0] bg-white px-3 py-2 text-xs text-[#64748B]">
            <span className="min-w-0 flex-1">
              {imageUploadSaving
                ? `Saving ${imageUploadPendingCount} photo${imageUploadPendingCount === 1 ? "" : "s"}...`
                : `${imageUploadFailedCount} photo upload${imageUploadFailedCount === 1 ? "" : "s"} failed`}
            </span>
            {!imageUploadSaving && imageUploadFailedCount > 0 && onRetryImageUploads && (
              <button type="button" onClick={onRetryImageUploads} className="font-semibold text-genie-primary hover:underline">
                Retry
              </button>
            )}
          </div>
        )}
        {statusText && surface !== "card" && (
          <p className="text-[11px] font-medium italic" style={{ color: brandColor }}>
            {statusText}
          </p>
        )}
        <GenieChatInputWeb
          value={inlineMessage}
          onChange={onChangeInlineMessage}
          onSend={onSend}
          disabled={isConnecting || attachments.some((attachment) => attachment.status === "analyzing")}
          hasAttachments={attachments.length > 0}
          onRemoveAttachment={onRemoveAttachment}
          onOpenFilePicker={onOpenFilePicker}
          brandColor={brandColor}
          secondaryColor={secondaryColor}
          backgroundColor={inputBackgroundColor}
          chatStyle={chatStyle}
          actionButtonStyle={actionButtonStyle}
        />
      </div>
    </>
  );

  if (surface === "card") {
    return (
      <div className="relative flex h-full w-full items-center justify-center">
        {/* The sheet blooms out of brand mist on mount, so the chat feels summoned.
            Kept subtle so it reads as a one-time bloom behind a persistent panel,
            not a busy backdrop competing with the sheet's own entrance. */}
        <GenieMist brandColor={cardBrand} secondaryColor={genieAccent} intensity={0.65} />
        <div
          className="relative flex h-full min-h-0 w-full flex-col overflow-hidden rounded-[28px] bg-white"
          style={{ boxShadow: cardShadow }}
        >
          {content}
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        inDrawer
          ? "flex h-full min-h-0 w-full flex-col"
          : "flex h-[70vh] min-h-[70vh] w-full flex-col overflow-hidden bg-transparent"
      }
      style={
        inDrawer
          ? backgroundStyle || { backgroundColor: "white" }
          : {
              // Full-height layout is only for conversations started from text.
              height: startedFromText ? "calc(100% - 8px)" : "70vh",
              minHeight: startedFromText ? undefined : "70vh",
              maxHeight: startedFromText ? "calc(100dvh - 100px)" : "70vh",
              background: "transparent",
            }
      }
    >
      {content}
    </div>
  );
};
